import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';
import { ChipInput, Form, Input } from '@engloba-tech/englobity';
import { NAME_SPACE } from 'i18n';
import { SelectInput, enumHelper, MONTH_TYPE } from 'shared';
import { useFormSendMailStyles } from './formSendMail.styles';

const MAIL_REGEX =
  /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;

export function FormSendMail({ formRef, onSubmit, errors }) {
  const { t } = useTranslation(NAME_SPACE.ANALYTICS_REPORTS);
  const classes = useFormSendMailStyles();
  const [inputs, setInputs] = useState({ mailsTo: [], ccs: [] });

  const months = useMemo(() => {
    return enumHelper.enumToCombo(MONTH_TYPE, t, 'months', true);
  }, [t]);

  async function handleSubmit() {
    onSubmit(inputs);
  }

  const handleAddMail = (target, mail = '') => {
    if (mail?.length) {
      setInputs(prevInputs => ({
        ...prevInputs,
        [target]: [
          ...new Set([
            ...prevInputs[target],
            ...mail.split(';').filter(element => element && element.match(MAIL_REGEX))
          ])
        ]
      }));
    }
  };

  const handleDeleteMail = (target, mailToDelete) => {
    if (mailToDelete) {
      setInputs(prevInputs => ({
        ...prevInputs,
        [target]: [...prevInputs[target].filter(mail => mail !== mailToDelete)]
      }));
    }
  };

  const handleChange =
    fieldName =>
    ({ target }) =>
      setInputs(prevInputs => ({ ...prevInputs, [fieldName]: target.value }));

  return (
    <Form className={classes.form} elementRef={formRef} onSubmit={handleSubmit} autoComplete="off" errors={errors}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography component="div" variant="body2">
            {t('sendByMail.description')}
          </Typography>
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <ChipInput
            label={t('sendByMail.properties.mailsTo')}
            required
            value={inputs.mailsTo || []}
            onAdd={mail => handleAddMail('mailsTo', mail)}
            onDelete={mail => handleDeleteMail('mailsTo', mail)}
            validators={['required']}
            errorMessages={[t('validations.required')]}
            className={classes.chip}
          />
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <ChipInput
            label={t('sendByMail.properties.ccs')}
            value={inputs.ccs || []}
            onAdd={mail => handleAddMail('ccs', mail)}
            onDelete={mail => handleDeleteMail('ccs', mail)}
            className={classes.chip}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <Input
            required
            name={t('sendByMail.properties.year')}
            label={t('sendByMail.properties.year')}
            variant="outlined"
            type="number"
            value={inputs.year ?? ''}
            onChange={handleChange('year')}
            inputProps={{ 'aria-label': t('sendByMail.properties.year') }}
            validators={['required', 'minNumber:2000', 'maxNumber:2150']}
            errorMessages={[
              t('validations.required'),
              t('validations.minNumber', { minimum: 2000 }),
              t('validations.maxNumber', { maximum: 2150 })
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <SelectInput
            name={t('sendByMail.properties.month')}
            label={t('sendByMail.properties.month')}
            elements={months}
            required
            value={inputs.month}
            onChange={handleChange('month')}
            validators={['required']}
            errorMessages={[t('validations.required')]}
          />
        </Grid>
      </Grid>
    </Form>
  );
}

FormSendMail.propTypes = {
  formRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  onSubmit: PropTypes.func.isRequired,
  errors: PropTypes.object
};
